import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { AdminStatCardFlex } from './adminUi'

const VIEW_TABS = [
  { id: 'active', label: 'Active requests' },
  { id: 'rejected', label: 'Rejected' },
]

const ACTIVE_STATUS_OPTIONS = [
  { id: 'all', label: 'All active' },
  { id: 'pending', label: 'Pending' },
  { id: 'sent', label: 'Sent' },
  { id: 'approved', label: 'Approved' },
]

const SORT_OPTIONS = [
  { id: 'created_at:desc', label: 'Newest first' },
  { id: 'created_at:asc', label: 'Oldest first' },
  { id: 'submitter:asc', label: 'Submitter A → Z' },
  { id: 'submitter:desc', label: 'Submitter Z → A' },
  { id: 'subject:asc', label: 'Subject A → Z' },
  { id: 'subject:desc', label: 'Subject Z → A' },
  { id: 'status:asc', label: 'Status A → Z' },
  { id: 'sent_at:desc', label: 'Recently sent' },
]

const ROLE_OPTIONS = [
  { id: 'all', label: 'All roles' },
  { id: 'GP', label: 'GP' },
  { id: 'LP', label: 'LP' },
  { id: 'Startup', label: 'Startup' },
  { id: 'ServiceProvider', label: 'Service Provider' },
]

const AUDIENCE_LABELS = {
  all: 'All users',
  gp: 'All GP',
  lp: 'All LP',
  startup: 'Startup',
  sp: 'Service Provider',
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(',', '')
}

function audienceLabel(value) {
  if (!value) return '—'
  return String(value)
    .split(',')
    .map((part) => AUDIENCE_LABELS[part.trim().toLowerCase()] || part.trim())
    .join(', ')
}

function statusClass(status) {
  if (status === 'sent') return 'success'
  if (status === 'rejected') return 'escalated'
  if (status === 'pending') return 'pending'
  return 'reviewing'
}

function parseContent(row) {
  try {
    return typeof row.content === 'string' ? JSON.parse(row.content) : row.content || {}
  } catch {
    return {}
  }
}

export default function AdminBulkEmail() {
  const [rows, setRows] = useState([])
  const [counts, setCounts] = useState({ active: 0, pending: 0, sent: 0, rejected: 0 })
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('active')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [audienceFilter, setAudienceFilter] = useState('all')
  const [sort, setSort] = useState('created_at:desc')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [busy, setBusy] = useState(false)

  const [sortBy, sortDir] = sort.split(':')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/email-campaigns', {
        params: {
          view,
          status: view === 'active' ? statusFilter : 'rejected',
          search: search.trim() || undefined,
          sortBy,
          sortDir,
          role: roleFilter,
          audience: audienceFilter,
        },
      })
      setRows(res.data?.items || [])
      setCounts(res.data?.counts || { active: 0, pending: 0, sent: 0, rejected: 0 })
      window.dispatchEvent(new Event('rs-admin-badges-changed'))
    } catch {
      toast.error('Failed to load bulk email requests')
    } finally {
      setLoading(false)
    }
  }, [view, statusFilter, search, sortBy, sortDir, roleFilter, audienceFilter])

  useEffect(() => {
    load()
  }, [load])

  const openDetail = async (id) => {
    setDetailLoading(true)
    setFeedback('')
    try {
      const res = await axios.get(`/api/admin/email-campaigns/${id}`)
      setDetail(res.data.request)
    } catch {
      toast.error('Failed to load request detail')
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleDecision = async (decision) => {
    if (!detail?.id) return
    setBusy(true)
    try {
      await axios.post(`/api/admin/email-approval/${detail.id}`, {
        decision,
        adminNotes: feedback.trim() || undefined,
        userFeedback: feedback.trim() || undefined,
      })
      toast.success(decision === 'approved' ? 'Request approved and emails sent' : 'Request moved to Rejected')
      setDetail(null)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete rejected request "${name || id}"? This cannot be undone.`)) return
    setBusy(true)
    try {
      await axios.delete(`/api/admin/email-campaigns/${id}`)
      toast.success('Rejected request deleted')
      if (detail?.id === id) setDetail(null)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  const resetFilters = () => {
    setStatusFilter('all')
    setRoleFilter('all')
    setAudienceFilter('all')
    setSort('created_at:desc')
    setSearchInput('')
    setSearch('')
  }

  const detailContent = detail?.content || {}
  const quota = detail?.quota

  return (
    <AdminLayout title="Bulk Email Management">
      <p style={{ margin: '0 0 16px', color: '#606266', fontSize: 13, lineHeight: 1.5 }}>
        Review member bulk email requests. Rejected requests are kept in a separate tab and can be permanently deleted.
      </p>

      <div className="admin-stats-row cols-4" style={{ marginBottom: 20 }}>
        <AdminStatCardFlex icon="email" cls="blue" label="Active" value={loading ? '…' : String(counts.active ?? 0)} />
        <AdminStatCardFlex icon="clock" cls="orange" label="Pending" value={loading ? '…' : String(counts.pending ?? 0)} />
        <AdminStatCardFlex icon="check" cls="green" label="Sent" value={loading ? '…' : String(counts.sent ?? 0)} />
        <AdminStatCardFlex icon="alert" cls="red" label="Rejected" value={loading ? '…' : String(counts.rejected ?? 0)} />
      </div>

      <div className="admin-period-bar" style={{ marginBottom: 16 }}>
        <div className="admin-period-tabs">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`admin-period-tab${view === tab.id ? ' active' : ''}`}
              onClick={() => {
                setView(tab.id)
                if (tab.id === 'rejected') setStatusFilter('all')
              }}
            >
              {tab.label}
              {tab.id === 'rejected' && counts.rejected > 0 ? ` (${counts.rejected})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-filter-bar" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        {view === 'active' ? (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {ACTIVE_STATUS_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
          </select>
        ) : null}
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          {ROLE_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
        </select>
        <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}>
          <option value="all">All audiences</option>
          <option value="lp">All LP</option>
          <option value="gp">All GP</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORT_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
        </select>
        <input
          type="search"
          placeholder="Search name, email, subject, body, feedback…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput) }}
          style={{ minWidth: 240 }}
        />
        <button type="button" className="admin-btn secondary" onClick={() => setSearch(searchInput)}>Search</button>
        <button type="button" className="admin-btn secondary" onClick={resetFilters}>Reset</button>
        <button type="button" className="admin-btn secondary" onClick={load}>Refresh</button>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">
            {view === 'rejected' ? 'Rejected requests' : 'Active requests'}
          </span>
          <span style={{ fontSize: 12, color: '#909399' }}>
            {loading ? 'Loading…' : `${rows.length} record(s)`}
          </span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Submitter</th>
              <th>Role</th>
              <th>Sender name</th>
              <th>Audience</th>
              <th>Subject</th>
              <th>Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#909399', padding: 28 }}>
                  {view === 'rejected' ? 'No rejected requests.' : 'No active requests match your filters.'}
                </td>
              </tr>
            ) : rows.map((row) => {
              const content = parseContent(row)
              return (
                <tr key={row.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.submitter_name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#909399' }}>{row.submitter_email}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>{row.submitter_role || '—'}</td>
                  <td>{row.sender_display_name || '—'}</td>
                  <td style={{ fontSize: 12 }}>{audienceLabel(row.target_audience)}</td>
                  <td>{content.subject || row.title}</td>
                  <td><span className={`admin-status-badge ${statusClass(row.status)}`}>{row.status}</span></td>
                  <td className="admin-td-actions">
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button type="button" className="admin-btn secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openDetail(row.id)}>
                        {row.status === 'pending' ? 'Review' : 'View'}
                      </button>
                      {view === 'rejected' && row.status === 'rejected' ? (
                        <button
                          type="button"
                          className="admin-btn secondary"
                          style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }}
                          disabled={busy}
                          onClick={() => handleDelete(row.id, content.subject || row.title)}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <AdminModal
        open={!!detail || detailLoading}
        title="Bulk email request"
        subtitle={detail?.submitter?.name ? `Submitted by ${detail.submitter.name}` : ''}
        wide
        onClose={() => !busy && setDetail(null)}
        footer={detail?.status === 'pending' ? (
          <>
            <button type="button" className="admin-btn secondary" disabled={busy} onClick={() => setDetail(null)}>Close</button>
            <button type="button" className="admin-btn secondary" disabled={busy} onClick={() => handleDecision('rejected')}>
              Reject
            </button>
            <button type="button" className="admin-btn primary" disabled={busy} onClick={() => handleDecision('approved')}>
              {busy ? 'Sending…' : 'Approve & Send'}
            </button>
          </>
        ) : (
          <>
            {detail?.status === 'rejected' ? (
              <button
                type="button"
                className="admin-btn secondary"
                disabled={busy}
                style={{ color: '#dc2626', borderColor: '#fecaca' }}
                onClick={() => handleDelete(detail.id, detailContent.subject || detail.title)}
              >
                Delete permanently
              </button>
            ) : null}
            <button type="button" className="admin-btn primary" onClick={() => setDetail(null)}>Close</button>
          </>
        )}
      >
        {detailLoading ? (
          <p>Loading…</p>
        ) : detail ? (
          <div>
            {quota ? (
              <div className="admin-card" style={{ marginBottom: 16, padding: 16 }}>
                <div className="admin-card-title" style={{ marginBottom: 12 }}>Member billing & quota</div>
                <div className="admin-settings-grid-2">
                  <div><strong>Membership</strong><div>{quota.isPaid ? `Active (${quota.planTier})` : 'Not paid'}</div></div>
                  <div><strong>Email feature</strong><div>{quota.emailFeatureEnabled ? 'Enabled' : 'Disabled'}</div></div>
                  <div><strong>Plan email quota</strong><div>{quota.unlimited ? 'Unlimited (Premium+)' : 'Credit-based'}</div></div>
                  <div><strong>Purchased sends</strong><div>{quota.purchasedCredits ?? 0}</div></div>
                  <div><strong>Remaining sends</strong><div>{quota.unlimited ? 'Unlimited' : (quota.remainingCredits ?? 0)}</div></div>
                  <div><strong>Successful sends</strong><div>{quota.successCount ?? 0}</div></div>
                </div>
              </div>
            ) : null}

            <div className="admin-settings-grid-2" style={{ marginBottom: 16 }}>
              <div><strong>Submitted</strong><div>{formatDateTime(detail.created_at)}</div></div>
              <div><strong>Status</strong><div><span className={`admin-status-badge ${statusClass(detail.status)}`}>{detail.status}</span></div></div>
              <div><strong>Sender display name</strong><div>{detail.sender_display_name || '—'}</div></div>
              <div><strong>Target audience</strong><div>{audienceLabel(detail.target_audience)} ({detail.target_count || 0} users)</div></div>
              <div><strong>Subject</strong><div>{detailContent.subject || detail.title}</div></div>
              <div><strong>Reviewed</strong><div>{formatDateTime(detail.reviewed_at)}</div></div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Email body</strong>
              <pre className="admin-code-block admin-code-block-scroll">{detailContent.body || '—'}</pre>
            </div>

            {detail.user_feedback ? (
              <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8 }}>
                <strong>Feedback to user</strong>
                <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{detail.user_feedback}</p>
              </div>
            ) : null}

            {detail.status === 'pending' ? (
              <label className="admin-field">
                <span className="admin-field-label">Feedback to user (optional on approve; shown on reject)</span>
                <textarea
                  className="admin-field-input"
                  rows={4}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g. Sent successfully to 128 LPs."
                />
              </label>
            ) : null}
          </div>
        ) : null}
      </AdminModal>
    </AdminLayout>
  )
}
